import { useState } from 'react'
import { useParams } from 'react-router-dom'

import type { LANG, WithLoadingState } from '@eshop/type'
import Slider from '@mui/material/Slider'

import i18n from './i18n'
import css from './index.module.css'

import { Checkbox } from '#/global/component/Form/Checkbox'
import { Input } from '#/global/component/Form/Input'

import { Events } from '#/global/helper/Events'

export interface CheckboxFilter extends F {
    selected: string[]
    type: 'CHECKBOX'
}

export type Filter = CheckboxFilter | SliderRangeFilter

export type FiltersSelectedValues<T extends string = string> = Record<T, string[] | number[]>
export interface FilterValue {
    count: number
    title: string
    value: string
}
export interface Props {
    filters: Filter[]
    handleChange: (filterId: string, selected: string[] | number[]) => void
    lang: LANG
    values: FiltersSelectedValues
}
export interface SliderRangeFilter extends F {
    type: 'RANGE'
}

interface F {
    id: string
    title: string
    type: 'CHECKBOX' | 'RANGE'
    values: FilterValue[]
}

export const ProductFilters = (props: WithLoadingState<Props>) => {
    const { filters, handleChange, lang, loadingState, values: selected } = props
    const { max, min, value: sliderValues } = getSliderValues(filters, selected)

    const { branchTitle } = useParams()

    const [open, setOpen] = useState(false)
    const [sliderValue, setSliderValue] = useState(sliderValues ?? [min, max])
    const [moving, setMoving] = useState(false)

    if (loadingState) {
        return <div className={`r-product-filter ${css.container}`} />
    }

    const t = i18n[lang]

    const handleCheckboxChange = (filterId: string, value: string, isChecked: boolean) => {
        const s = [...selected[filterId]] as string[]
        if (isChecked) {
            s.push(value)
        } else {
            const index = s.indexOf(value)
            s.splice(index, 1)
        }
        return handleChange(filterId, s)
    }

    const toggleOpen = (newOpen: boolean = !open) => setOpen(newOpen)

    const chooseStore = (
        <div className={css.chooseStore}>
            {t.chooseStoreTitle}
            <button
                className={`dek-button-blue ${css.button} dek-button-small`}
                onClick={() => Events.openStorePicker()}
                role="button"
                type="button">
                {t.chooseStoreButton}
            </button>
        </div>
    )

    return (
        <div className={`r-product-filter ${css.container} ${open ? css.open : ''}`.trim()}>
            <div className={css.title} onClick={() => toggleOpen()}>
                <div>
                    <img src="https://cdn1.idek.cz/file/settings-69267865.svg" width={14} height={14} />
                    {t.specificParams}
                </div>
                <img src="https://cdn1.idek.cz/file/icon-down-open-25d60d8e.svg" width={12} height={10} />
            </div>
            <div className={css.content}>
                {filters.map((filter, index) => {
                    if (filter.type === 'CHECKBOX') {
                        return (
                            <div key={index}>
                                <h3 className={css.label}>{filter.title}</h3>
                                {filter.id === 'AVAILABILITY' && !branchTitle && chooseStore}
                                <div className={css.filterList}>
                                    {filter.values.map((value, index) => (
                                        <div key={index} className={css.filter}>
                                            <Checkbox
                                                disabled={value.count === 0}
                                                isChecked={(selected[filter.id] as string[]).includes(value.value)}
                                                onChange={isChecked => handleCheckboxChange(filter.id, value.value, isChecked)}>
                                                <div className={css.checkboxText}>
                                                    <div>{value.title}</div>
                                                    <div>{value.count}</div>
                                                </div>
                                            </Checkbox>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    }
                    if (filter.type === 'RANGE') {
                        const maxValue = sliderValues || moving ? (sliderValue[1] === Infinity ? max : sliderValue[1]) : max
                        const minValue = sliderValues || moving ? sliderValue[0] || min : min
                        return (
                            <div key={index}>
                                <h3 className={css.label}>{filter.title}</h3>
                                <div className={css.slider}>
                                    <Slider
                                        max={max}
                                        min={min}
                                        value={[minValue, maxValue]}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={value => value.toLocaleString('cs-CZ')}
                                        onChange={(_, newValue) => {
                                            setMoving(true)
                                            setSliderValue(newValue as number[])
                                        }}
                                        onChangeCommitted={(_, newValue) => {
                                            setMoving(false)
                                            handleChange(filter.id, newValue as number[])
                                        }}
                                        sx={{
                                            'color': 'var(--red)',
                                            '& .MuiSlider-thumb': {
                                                background: '#fff',
                                                border: '4px solid var(--red)',
                                                width: '27px',
                                                height: '27px',
                                            },
                                            '& .MuiSlider-thumb:hover': {
                                                boxShadow: '0px 0px 0px 8px rgb(226 0 26 / 16%)',
                                            },
                                            '& .MuiSlider-thumb.Mui-focusVisible': {
                                                boxShadow: '0px 0px 0px 8px rgb(226 0 26 / 16%)',
                                            },
                                            '& .MuiSlider-thumb.Mui-active': {
                                                boxShadow: '0px 0px 0px 14px rgb(226 0 26 / 16%)',
                                            },
                                            '& .MuiSlider-track': {
                                                border: 'none',
                                            },
                                        }}
                                    />
                                    <div className={css.sliderInputs}>
                                        <Input
                                            max={maxValue}
                                            min={min}
                                            onBlur={() => handleChange(filter.id, sliderValue)}
                                            onChange={value => setSliderValue([+value, sliderValue[1]])}
                                            size="xsmall"
                                            type="number"
                                            value={minValue}
                                        />
                                        <Input
                                            max={max}
                                            min={minValue}
                                            onBlur={() => handleChange(filter.id, sliderValue)}
                                            onChange={value => setSliderValue([sliderValue[0], +value])}
                                            size="xsmall"
                                            type="number"
                                            value={maxValue}
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    return null
                })}
                <div className={css.footer}>
                    <div className={css.close} onClick={() => toggleOpen(false)}>
                        {t.close}
                    </div>
                    {Object.values(selected).find(filter => filter.length > 0) && (
                        <div className={css.clear}>
                            <a href={void 0} onClick={() => handleChange('ALL', [])}>
                                {t.clearAll}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const getSliderValues = (filters: Filter[] | undefined, selected: Partial<FiltersSelectedValues> | undefined) => {
    let min = 0
    let max = Infinity
    let value: number[] | undefined
    const filter = filters?.find(f => f.type === 'RANGE') as SliderRangeFilter | undefined
    if (filter) {
        min = Math.floor(+filter.values[0].value)
        max = Math.ceil(+filter.values[1].value)
        if (selected && selected[filter.id] && selected[filter.id]?.length === 2) {
            value = selected[filter.id] as number[]
        }
    }
    return { min, max, value }
}
